import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { AlertTriangle, Calendar, ClipboardCheck, Scale, Loader2 } from "lucide-react";
import Avatar from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";

const ACTION_LABELS = {
  coaching: "Coaching",
  verbal: "Verbal Warning",
  written: "Written Warning",
  final: "Final Warning",
  termination: "Termination",
};
const REVIEW_LABELS = { "30_day": "30-day", "60_day": "60-day", "90_day": "90-day", annual: "Annual", custom: "Custom" };

export default function HR() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/hr/overview")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load HR overview"));
  }, []);

  return (
    <div>
      <Topbar title="HR" />
      <div className="px-4 sm:px-10 pb-12 sm:pb-16 space-y-6 stagger-fade" data-testid="hr-page">
        <header>
          <div className="label-eyebrow mb-1">People care · rollup</div>
          <h2 className="font-serif-display text-2xl text-stone-900">Reviews & Discipline</h2>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl leading-relaxed">
            Upcoming reviews in the next 30 days and discipline records logged in the last 90 days.
            Rainbow leadership sees all four boutiques; GMs see their own.
          </p>
        </header>

        {err && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 flex items-center gap-2" data-testid="hr-error">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        {!data && !err && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hr-stats">
              <MiniStat label="Upcoming reviews · 30 days" value={data.totals.upcoming_reviews} icon={Calendar} />
              <MiniStat label="Discipline · last 90 days" value={data.totals.recent_discipline} icon={Scale} />
              <MiniStat label="Active discipline records" value={data.totals.active_discipline} icon={AlertTriangle} tone={data.totals.active_discipline > 0 ? "amber" : "stone"} />
              <MiniStat label="Scope" value={data.scope === "rainbow_wide" ? "All boutiques" : data.scope} icon={ClipboardCheck} />
            </div>

            <section className="bg-white subtle-border rounded-3xl card-shadow overflow-hidden" data-testid="hr-reviews-section">
              <header className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
                <div>
                  <h3 className="font-serif-display text-lg text-stone-900">Upcoming reviews</h3>
                  <div className="label-eyebrow mt-0.5">{data.upcoming_reviews.length} due within 30 days</div>
                </div>
              </header>
              {data.upcoming_reviews.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-stone-400 italic" data-testid="hr-reviews-empty">
                  No reviews on the horizon. Enjoy the calm.
                </div>
              ) : (
                <div className="divide-y divide-stone-50">
                  {data.upcoming_reviews.map((r) => {
                    const overdue = new Date(r.due_date) < new Date();
                    return (
                      <div key={r.id} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`hr-review-${r.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={r.employee_name} boutique={r.boutique_code} size={28} />
                          <div className="min-w-0">
                            <div className="text-stone-900 truncate">{r.employee_name}</div>
                            <div className="text-[11px] text-stone-500">
                              {REVIEW_LABELS[r.review_type]} · {r.boutique_code}
                            </div>
                          </div>
                        </div>
                        <div className={`text-xs shrink-0 ml-3 ${overdue ? "text-rose-600 font-medium" : "text-stone-500"}`}>
                          {overdue ? "Overdue · " : "Due "}{r.due_date}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="bg-white subtle-border rounded-3xl card-shadow overflow-hidden" data-testid="hr-discipline-section">
              <header className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
                <div>
                  <h3 className="font-serif-display text-lg text-stone-900">Recent discipline</h3>
                  <div className="label-eyebrow mt-0.5">{data.recent_discipline.length} in the last 90 days</div>
                </div>
              </header>
              {data.recent_discipline.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-stone-400 italic" data-testid="hr-discipline-empty">
                  No discipline logged this quarter. A quiet quarter.
                </div>
              ) : (
                <div className="divide-y divide-stone-50">
                  {data.recent_discipline.map((d) => {
                    const sigs = d.signatures || {};
                    const complete = ["employee", "mgmt"].every((k) => sigs[k]);
                    return (
                      <div key={d.id} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`hr-discipline-${d.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={d.employee_name} boutique={d.boutique_code} size={28} />
                          <div className="min-w-0">
                            <div className="text-stone-900 truncate">{d.employee_name}</div>
                            <div className="text-[11px] text-stone-500 truncate">
                              {ACTION_LABELS[d.action_type]}
                              {d.sop_reference ? ` · ${d.sop_reference}` : ""}
                              {" · "}{d.boutique_code}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs shrink-0 ml-3">
                          <span className={complete ? "text-emerald-700" : "text-amber-700"}>
                            {complete ? "signed" : "pending sig"}
                          </span>
                          <span className="text-stone-400 ml-2">{(d.logged_at || "").slice(0, 10)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, tone = "stone" }) {
  const toneClass = tone === "amber" ? "border-amber-200 bg-amber-50/60" : "subtle-border bg-white";
  return (
    <div className={`rounded-2xl p-4 ${toneClass}`}>
      <div className="flex items-center gap-2 label-eyebrow mb-2">
        {Icon && <Icon size={11} strokeWidth={1.5} />}
        <span>{label}</span>
      </div>
      <div className="font-serif-display text-2xl text-stone-900">{value}</div>
    </div>
  );
}
